/**
 * Analyzer: Day "One Good Trade" Counter (Wave B.4, v7.4.4)
 *
 * SMB Capital's process-over-outcome framework from "One Good Trade"
 * (Bellafiore). A "Good Process Trade" is one where you DID THE RIGHT
 * THINGS regardless of whether it won. The point is to track process
 * separately from P&L so a green day doesn't validate sloppy execution
 * and a red day doesn't punish disciplined execution.
 *
 * A trade qualifies as Good Process when ALL of:
 *   1. Has at least one tagged confluence (you knew WHY you took it)
 *   2. Has a defined stop loss (you knew WHERE you were wrong)
 *   3. No user-tagged mistakes
 *   4. No auto-detected `panic-exit` flag (Wave A) — bailed before invalidation
 *   5. No auto-detected `averaged-down` flag (Wave B.3) — added to a loser
 *
 * Surfaces a SUCCESS observation when Good Process count exceeds the
 * winner count (you executed well even if the market didn't cooperate).
 * Surfaces a WARNING when fewer than half the trades met the bar
 * (discipline gap — outcomes are not the right yardstick).
 *
 * Hooks into day-scope; surfaces alongside the existing day grade.
 *
 * Reads:
 *   day.trades — enriched trades with .netPnl
 *   trade.confluences, trade.mistakes, trade.stopLoss, trade.stopLossPoints
 *   trade.barAnalysis (for panic-exit + averaged-down flag computation)
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';
import { computeTradeFlags } from '../../../barAnalysis/tradeFlags.js';

export const key = 'dayOneGoodTrade';
export const tier = 'free';

const MIN_TRADES_TO_SCORE = 3;

const hasDefinedStop = (trade) => {
  const stopLoss = parseFloat(trade?.stopLoss);
  if (Number.isFinite(stopLoss) && stopLoss > 0) return true;
  const stopPts = parseFloat(trade?.stopLossPoints);
  return Number.isFinite(stopPts) && stopPts > 0;
};

const hasTaggedConfluence = (trade) => {
  return Array.isArray(trade?.confluences) && trade.confluences.length > 0;
};

const hasNoTaggedMistakes = (trade) => {
  return !Array.isArray(trade?.mistakes) || trade.mistakes.length === 0;
};

const hasProcessViolationFlag = (trade) => {
  const flags = computeTradeFlags(trade);
  return flags.some(f => f.key === 'panic-exit' || f.key === 'averaged-down');
};

/**
 * Did this trade meet the Good Process bar?
 */
export const isGoodProcess = (trade) => {
  if (!trade) return false;
  if (!hasTaggedConfluence(trade)) return false;
  if (!hasDefinedStop(trade)) return false;
  if (!hasNoTaggedMistakes(trade)) return false;
  if (hasProcessViolationFlag(trade)) return false;
  return true;
};

const isWinner = (trade) => Number.isFinite(trade?.netPnl) && trade.netPnl > 0;

export const analyze = (ctx) => {
  const { day } = ctx || {};
  if (!day?.trades || !Array.isArray(day.trades)) return null;
  if (day.trades.length < MIN_TRADES_TO_SCORE) return null;

  const trades = day.trades;
  const goodCount = trades.filter(isGoodProcess).length;
  const winnerCount = trades.filter(isWinner).length;
  const total = trades.length;

  // Edge case: zero good process trades is its own signal — never null on this
  // case (the warning IS the point).
  const goodRatio = goodCount / total;

  // SUCCESS path: Good Process count exceeds winner count → executed well
  // even if outcomes lagged. The signal SMB explicitly wants traders to
  // celebrate over green-day high-fives.
  if (goodCount > winnerCount && goodCount >= MIN_TRADES_TO_SCORE) {
    return makeObservation({
      id: `${key}:${day.date}:process-over-outcome`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.PROCESS,
      title: `${goodCount} Good Process trades · ${winnerCount} winners`,
      detail: `${goodCount} of ${total} trades met the process bar today (confluence + stop + no mistakes + no process-violation flags). That's more than the ${winnerCount} winners — you ran your playbook, the outcomes will follow.`,
      action: 'Write today\'s good-process count in your journal alongside the P&L. Over time these are what compound, not the daily green/red.',
      evidence: { goodCount, winnerCount, total, goodRatio },
      confidence: 0.85,
      priority: 65,
    });
  }

  // WARNING path: low Good Process ratio → discipline gap regardless of P&L
  if (goodRatio < 0.5) {
    return makeObservation({
      id: `${key}:${day.date}:discipline-gap`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.WARNING,
      category: CATEGORY.PROCESS,
      title: `Only ${goodCount} of ${total} trades met process criteria`,
      detail: `${total - goodCount} trades had missing confluence, missing stop, tagged mistakes, or auto-detected process violations (panic exit / averaged down). The day grade reflects outcomes; this is the leading indicator.`,
      action: 'Pick ONE missing process item and make it a hard rule for next session — most common gaps are "no confluence tagged" (didn\'t name the why) and "no stop set" (no defined invalidation).',
      evidence: { goodCount, winnerCount, total, goodRatio },
      confidence: 0.80,
      priority: 72,
    });
  }

  // Neutral middle — Good Process tracking goes silent. Keeps the feed from
  // getting noisy on average days.
  return null;
};
